import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, Overline, Tag } from "@/components/cathedral/Primitives";
import AutoLink from "@/components/cathedral/AutoLink";

/**
 * ReflectionMemoryForesight · Architect Console hero panel
 *
 * Three live tiles, pulled from existing endpoints (no new backend work):
 *
 *  REFLECTION — the Cathedral's most recent ledger event in plain language.
 *               What the spine just "thought."
 *
 *  MEMORY     — counts of what the Cathedral knows: ledger entries, deliberations,
 *               directives, active nodes. The total weight of the lattice.
 *
 *  FORESIGHT  — derived from truth_confidence + recent gate-failure rate.
 *               What the Cathedral is projecting next.
 *
 * Refreshes every 10s in step with the rest of the Console.
 */
export default function ReflectionMemoryForesight() {
  const [stats, setStats] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [engine, setEngine] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    const fetchAll = async () => {
      try {
        const [s, l, e] = await Promise.all([
          api.stats(),
          api.ledger({ limit: 8 }),
          api.truthEngineState().catch(() => null),
        ]);
        if (!alive) return;
        setStats(s);
        setLedger(l);
        setEngine(e);
      } catch (e) {
        if (alive) setErr(e.message);
      }
    };
    fetchAll();
    const t = setInterval(fetchAll, 10000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // ── Reflection: humanize the most recent ledger entry ───────────────
  const reflection = useMemo(() => {
    const entry = ledger?.entries?.[0] || stats?.recent_entries?.[0];
    if (!entry) return null;
    const kind = entry.kind || "event";
    const verb = ({
      doctrine_ingest: "ingested the doctrine",
      gumroad_credit_received: "received a Gumroad credit",
      license_redeemed: "sealed a redemption",
      ring_deliberation: "called the Ring",
      chat_message: "spoke with a traveler",
      architect_gumroad_url_mapping: "mapped a storefront",
      hollow_vessel_check: "checked the Hollow Vessel",
      truth_gate_pass: "passed the 95% Gate",
      truth_gate_fail: "failed the 95% Gate",
    })[kind] || `recorded a ${kind.replace(/_/g, " ")}`;
    const subject =
      entry.payload?.title ||
      entry.payload?.persona ||
      entry.payload?.code ||
      entry.payload?.node_id ||
      entry.payload?.directive_id ||
      "";
    const when = entry.timestamp ? new Date(entry.timestamp) : null;
    return { verb, subject, when, kind };
  }, [ledger, stats]);

  // ── Memory: aggregate counts ─────────────────────────────────────────
  const memory = stats
    ? [
        { k: "Ledger Entries",  v: stats.ledger_entries ?? 0 },
        { k: "Deliberations",   v: stats.deliberations_logged ?? 0 },
        { k: "Directives",      v: stats.directives_loaded ?? 0 },
        { k: "Active Nodes",    v: stats.active_nodes ?? 0 },
      ]
    : null;

  // ── Foresight: derived from confidence + recent gate failures + engine θ ─
  const foresight = useMemo(() => {
    if (!stats) return null;
    // Blend the static avg_truth_confidence with the live engine θ when
    // available — the lattice's actual learning influences the projection.
    const baseConf = (stats.avg_truth_confidence || 0) * 100;
    const engineTheta = engine?.initialized && typeof engine.theta === "number"
      ? Math.max(0, Math.min(1, engine.theta)) * 100
      : null;
    const conf = Math.round(
      engineTheta !== null ? 0.6 * baseConf + 0.4 * engineTheta : baseConf
    );
    const fails = stats.truth_gate_failures || 0;
    let mood, glow, tag;
    if (conf >= 95 && fails === 0) {
      mood = "Continuity sustained · the spine projects stability.";
      glow = "cyan"; tag = "STABLE";
    } else if (conf >= 90) {
      mood = "Lattice holding · minor drift possible. Recommend Ring of Three.";
      glow = "gold"; tag = "WATCHFUL";
    } else if (conf >= 80) {
      mood = "Confidence drifting · the Cathedral expects re-verification soon.";
      glow = "violet"; tag = "DRIFT";
    } else {
      mood = "Gate strain detected · risk of dissolution if uncorrected.";
      glow = "red"; tag = "STRAIN";
    }
    return { conf, fails, mood, glow, tag, engineTheta };
  }, [stats, engine]);

  const timeAgo = (when) => {
    if (!when) return "—";
    const seconds = Math.max(0, Math.floor((Date.now() - when.getTime()) / 1000));
    if (seconds < 60) return `${seconds}s ago`;
    const m = Math.floor(seconds / 60);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    return `${d}d ago`;
  };

  if (err) {
    return (
      <Card glow="red" className="p-5 mb-10" data-testid="rmf-error">
        <p className="font-mono text-xs text-glow-red">RMF panel unreachable · {err}</p>
      </Card>
    );
  }

  return (
    <div
      className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-10"
      data-testid="rmf-panel"
    >
      {/* REFLECTION */}
      <Card glow="violet" className="p-6" data-testid="rmf-reflection">
        <Overline>Reflection</Overline>
        <div className="mt-3 min-h-[88px]">
          {reflection ? (
            <>
              <p className="font-display text-[15px] leading-snug text-glow-violet">
                The Cathedral <span className="text-[var(--text)]">{reflection.verb}</span>
                {reflection.subject ? (
                  <>: <span className="text-[var(--text-2)] italic"><AutoLink text={reflection.subject} /></span></>
                ) : null}
                .
              </p>
              <div className="mt-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)]">
                <span>{timeAgo(reflection.when)}</span>
                <span>·</span>
                <span className="text-glow-violet">{reflection.kind}</span>
              </div>
            </>
          ) : (
            <p className="font-mono text-[11px] text-[var(--text-3)]">Listening…</p>
          )}
        </div>
      </Card>

      {/* MEMORY */}
      <Card glow="cyan" className="p-6" data-testid="rmf-memory">
        <Overline>Memory</Overline>
        <div className="mt-3 min-h-[88px]">
          {memory ? (
            <ul className="space-y-1.5">
              {memory.map(({ k, v }) => (
                <li
                  key={k}
                  className="flex items-baseline justify-between font-mono text-[11px]"
                >
                  <span className="uppercase tracking-[0.2em] text-[var(--text-3)]">
                    {k}
                  </span>
                  <span className="font-display text-glow-cyan text-base">{v}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="font-mono text-[11px] text-[var(--text-3)]">Counting…</p>
          )}
        </div>
      </Card>

      {/* FORESIGHT */}
      <Card
        glow={foresight?.glow || "gold"}
        className="p-6"
        data-testid="rmf-foresight"
      >
        <div className="flex items-center justify-between">
          <Overline>Foresight</Overline>
          {foresight && (
            <Tag
              tone={
                foresight.tag === "STABLE"
                  ? "pass"
                  : foresight.tag === "STRAIN"
                  ? "conflict"
                  : "warn"
              }
            >
              {foresight.tag}
            </Tag>
          )}
        </div>
        <div className="mt-3 min-h-[88px]">
          {foresight ? (
            <>
              <div className="flex items-baseline gap-3">
                <span
                  className={`font-display text-3xl text-glow-${foresight.glow} leading-none`}
                >
                  {foresight.conf}%
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)]">
                  truth confidence
                </span>
              </div>
              <p className="mt-3 font-mono text-[10px] leading-relaxed text-[var(--text-2)]">
                {foresight.mood}
              </p>
              {foresight.fails > 0 && (
                <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.22em] text-glow-red">
                  {foresight.fails} gate fail{foresight.fails === 1 ? "" : "s"} on record
                </p>
              )}
            </>
          ) : (
            <p className="font-mono text-[11px] text-[var(--text-3)]">Projecting…</p>
          )}
        </div>
      </Card>

      {/* LATTICE — Self-Adaptive Truth Engine */}
      <Card glow="gold" className="p-6" data-testid="rmf-lattice">
        <div className="flex items-center justify-between">
          <Overline>Lattice</Overline>
          {engine?.initialized && <Tag tone="pass">ADAPTIVE</Tag>}
        </div>
        <div className="mt-3 min-h-[88px]">
          {engine?.initialized ? (
            <>
              <div className="flex items-baseline gap-3">
                <span className="font-display text-3xl text-glow-gold leading-none">
                  θ {engine.theta?.toFixed(3)}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)]">
                  step {engine.step}
                </span>
              </div>
              <div className="mt-3 space-y-1 font-mono text-[10px]">
                {(engine.layer_states || []).map((ls, i) => (
                  <div
                    key={`layer-${i}-${ls.alpha ?? "x"}`}
                    className="flex items-center justify-between text-[var(--text-3)] uppercase tracking-[0.18em]"
                  >
                    <span>L{i}</span>
                    <span className="text-[var(--text-2)]">
                      α {ls.alpha?.toFixed(3)} · β {ls.beta?.toFixed(3)}
                    </span>
                  </div>
                ))}
              </div>
              <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--text-3)]">
                3 layers · 12 rings · self-tuning
              </p>
            </>
          ) : (
            <p className="font-mono text-[11px] text-[var(--text-3)]">
              Engine cold · awaiting first Ring deliberation.
            </p>
          )}
        </div>
      </Card>
    </div>
  );
}
